import {  ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, Subject, takeUntil, take } from 'rxjs';
import { HhsgSharedAdminBaseComponent } from '../../common/base.component';
import { HhsgSharedAdminTab, HhsgSharedUserApplicationModel, HhsgSharedUserProfile } from '../../../models';
import { HhsgSharedApplication } from '../../../models/application.model';
import { HhsgSharedClientApplication } from '../../../models/client-application.model';
import { HhsgSharedClient } from '../../../models/client.model';
import { HhsgSharedCorporation } from '../../../models/corporation.model';
import { HhsgSharedStuderEmployee } from '../../../models/sgportal-data-api-models/employee.model';
import { User, UserRole } from '../../../models/user.model';
import {
    HhsgSharedApplicationsService,
    HhsgSharedClientAdminService,
    HhsgSharedConfigService,
    HhsgSharedCorporationService,
    HhsgSharedFormStateService,
    HhsgSharedStuderEmployeeService,
    HhsgSharedTabControlService,
    HhsgSharedUserAdministratorService,
    HhsgSharedUserAdminService,
    HhsgSharedUserService
} from '../../../services';
import { Constants, LoginMethodSettingConstants, UserRoles } from '../../../utilities/constants.util';
import { RoleUtil } from '../../../utilities/role.util';
import { HhsgSharedTabInterface } from '../../common/tab-control/tab.interface';
import { DevExtremeUtils } from '../../../utilities/dev-extreme.util';

@Component({
    template: ''
})
export abstract class HhsgSharedEditUserBase extends HhsgSharedAdminBaseComponent implements OnDestroy, OnInit, HhsgSharedTabInterface {
    @Input() tab: HhsgSharedAdminTab;

    public applications: HhsgSharedApplication[] = [];
    public userApplications: { clientId: number, applicationId: number }[] = [];
    public editableUserRoles: UserRole[];
    public errorMessages: string[] = [];
    public formGroup: UntypedFormGroup;
    public isAdding = false;
    public isEditing = false;
    public isShowDormant = false;
    public isLockedOutLabelText = "Is Locked Out";
    public isFormDisabled = false;
    public dxItemEditorOptions: any = DevExtremeUtils.dxGlobalItemEditorOptions;
    public disableEmail: boolean = true;
    public isEmailValid:boolean = false;
    public duplicateEmailError: boolean = false;
    
    protected _activeUser = new User();
    protected addedApplications: HhsgSharedApplication[] = [];
    protected addedClientApplications: HhsgSharedClientApplication[] = [];
    protected clients: HhsgSharedClient[] = [];
    protected corpIdFromUrlParam: number;
    protected corporations: HhsgSharedCorporation[] = [];
    protected isLoading: boolean;
    protected isStuderDBAvailable = false;
    protected isDormantOriginalValue = false;
    protected loggedInUserProfile: HhsgSharedUserProfile;
    protected selectedEmployee: HhsgSharedStuderEmployee;
    protected studerAbbreviations: string[] = [Constants.VMAbbreviation, Constants.PCMAbbreviation, Constants.LEMAbbreviation];
    protected userAdministratorApplicationIds: number[] = [];
    protected userAdministratorClientIds: number[] = [];
    protected userApplicationModels: HhsgSharedUserApplicationModel[] = [];
    protected userIdFromUrlParam: number;
    protected _unsubscribe$ = new Subject();
    
    protected readonly applicationControlPrefix = "applicationCheckbox";
    protected readonly clientControlPrefix = "clientCheckbox";
    public ssoNameIdPattern: string = "^[a-zA-Z0-9@_\\-.()\\\/\*]*$";

    protected abstract initializeFormGroup(): void;
    protected abstract initializeControls(): void;
    protected abstract getIsFormGroupDirty(): boolean;

    constructor(
        public _activatedRouteBase: ActivatedRoute,
        public _applicationServiceBase: HhsgSharedApplicationsService,
        public _clientAdminServiceBase: HhsgSharedClientAdminService,
        public _configServiceBase: HhsgSharedConfigService,
        public _corporationServiceBase: HhsgSharedCorporationService,
        public _employeeServiceBase: HhsgSharedStuderEmployeeService,
        public _formStateServiceBase: HhsgSharedFormStateService,
        public _routerBase: Router,
        public _tabControlServiceBase: HhsgSharedTabControlService,
        public _userAdministratorServiceBase: HhsgSharedUserAdministratorService,
        public _userAdminServiceBase: HhsgSharedUserAdminService,
        public _userServiceBase: HhsgSharedUserService,
        public _formBuilderBase: UntypedFormBuilder,
        protected _changeDetectorBase: ChangeDetectorRef) {
        super(_formStateServiceBase, _routerBase);

        this.formGroup = new UntypedFormGroup({
        });
        
        this.initializeFormGroup();
        this._formBuilderBase.group(this.formGroup);

        this._activatedRouteBase.params.subscribe(async (params) => {
            this.userIdFromUrlParam = params.userId;
            this.corpIdFromUrlParam = params.corporationId;

            if (this.userIdFromUrlParam > 0) {
                this.isEditing = true;
            }
            else {
                this.isAdding = true;
                this.disableEmail = false;
            }

            if (this.corpIdFromUrlParam > 0) {
                this._userAdminServiceBase.selectedCorporationId = Number(this.corpIdFromUrlParam);
            }
        });

        let sgPortalApiServer = this._configServiceBase.appSettings.sgPortalApiServer;
        this.isStuderDBAvailable = sgPortalApiServer && sgPortalApiServer > '';

        this._userAdminServiceBase.UserClients = [];

        if (this._userAdminServiceBase.selectedUser) {
            this.activeUser = { ...this._userAdminServiceBase.selectedUser } as User;
        }
        else if (this.userIdFromUrlParam > 0) {
            this.setActiveUserIfUndefined();
        }
        else if (this._userServiceBase.childApplicationUser){
            this.getChildApplicationUserDetails();
         }
        else {
            this.activeUser = new User();
            this.setDefaultUserRole();
        }

        if (this.activeUser && this.activeUser.isDormant) {
            this.isLockedOutLabelText = "Is Locked Out (Dormant)";
            this.isDormantOriginalValue = true;
        }
    }

    public ngOnDestroy(): void {
        this._unsubscribe$.complete();
        this._userAdminServiceBase.selectedUser = undefined;
        this._userAdminServiceBase.UserEmployees = [];
        this._userAdminServiceBase.ClientWithApplications = undefined;
        this._userAdminServiceBase.ClientsWithApplications = [];
        this._formStateServiceBase.reset();
        this._userServiceBase.convertChildApplicationUserToHapUser = false;
        this._userServiceBase.childApplicationUser = null;
    }

    public async ngOnInit(): Promise<void> {
        this._userServiceBase.getUserProfile()
            .pipe(take(1))
            .subscribe(async (userProfile: HhsgSharedUserProfile) => {
                this.loggedInUserProfile = userProfile;

                if (userProfile) {
                    this.userAdministratorApplicationIds = userProfile.userApplications?.map(({ applicationId }) => applicationId);

                    if (this._userAdminServiceBase.selectedCorporation) {
                        this._userAdminServiceBase.selectedCorporationId = this._userAdminServiceBase.selectedCorporation.id
                    }

                    if (!this.userAdministratorApplicationIds) {
                        this.userAdministratorApplicationIds = [];
                    }

                    this.loadData();

                    await this._userAdminServiceBase.getEditableUserRoles(this._userAdminServiceBase.selectedCorporationId).then((corporationUserRoles) => {
                        this.editableUserRoles = corporationUserRoles.userRoles;
                        this.isFormDisabled = this.isEditing && !corporationUserRoles.userRoles.some(r => r.roleId == this._activeUser?.userRole.roleId);
                    });
                    
                    this._changeDetectorBase.markForCheck();
                }
            });
        this._userAdminServiceBase.isSubmitted = false;
        this._userAdminServiceBase.confirmSaving = false;
        this._changeDetectorBase.markForCheck();

        this._tabControlServiceBase
        .tabSelected$      
        .pipe(takeUntil(this._unsubscribe$))
        .subscribe(() => {
            if(this.getIsFormGroupDirty){          
                this.cancel();
            }           
        });
    }

    public get activeUser(): User {
        if (this._activeUser && this._activeUser.isDormant) {
            this._activeUser.isLockedOut = true;
        }

        return this._activeUser;
    }

    public set activeUser(user: User) {
        if (user) {
            this._activeUser = { ...user } as User; // Create a copy so updates aren't passed back to the client list            
        }

        this.resetMessages();

        if (this.formGroup) {
            this.formGroup.markAsPristine();
            this._formStateServiceBase.isFormDirty = false;
        }
        this._changeDetectorBase.markForCheck();
    }

    protected addAClient(clientId: number): void {
        var index: number = this._userAdminServiceBase.ClientsWithApplications.findIndex(x => x.client.id === clientId);
        var client = this.clients.find(x => x.id === clientId);
        this._userAdminServiceBase.UserClients.push({ client, index });

        let applications = this._userAdminServiceBase.ClientsWithApplications.find(c => c.client.id == clientId)?.clientApplications;

        if (applications) {
            applications.forEach(a => {
                this.addApplicationControl(clientId, a.applicationId);
            });
        }
    }

    protected addApplicationControl(clientId: number, applicationId: number): void {
        this.formGroup.addControl(
            this.getApplicationControlName(clientId, applicationId),
            this._formBuilderBase.control({ disabled: this.isFormDisabled, value: this.isApplicationChecked(clientId, applicationId) }, null)
        );
    }

    protected addClientControl(clientId: number): void {
        this.formGroup.addControl(
            this.getClientControlName(clientId),
            this._formBuilderBase.control({ disabled: this.isFormDisabled, value: this.isClientChecked(clientId) }, null)
        );
    }

    public anyActiveClientApplications(clientId: number): boolean {
        var clientApps = this._userAdminServiceBase.ClientsWithApplications.find(c => c.client.id === clientId).clientApplications;

        if (!clientApps) {
            return false;
        }

        return clientApps.some(h => h.isActive && h.isPurchased);
    }

    public anyInactiveClientApplications(clientId: number): boolean {
        var clientApps = this._userAdminServiceBase.ClientsWithApplications.find(x => x.client.id === clientId).clientApplications;

        if (!clientApps) {
            return false;
        }

        return (clientApps.findIndex(x => !x.isActive && x.isPurchased) > -1);
    }

    public async cancel(): Promise<void> {
        this.setLoadingStatus(true);
        this.activeUser.corporationId = null;
        await this.resetRole();
        if (this.isEditing) {
            await this.reload();
        }
        this.setLoadingStatus(false);
    }

    protected checkAtleastOneApplicationSelectedForSelectedClient(): boolean {
        let applicationSelected = false;

        for (let userClient of this._userAdminServiceBase.UserClients) {
            applicationSelected = false;

            let userApplications = this.activeUser.userApplicationModels.filter(x => x.clientId == userClient.client.id);

            if (userApplications?.length > 0) {
                applicationSelected = userApplications?.some(u => u.applicationAbbreviation != Constants.HuronAcceleratorsAbbreviation);

                if (!applicationSelected) {
                    break;
                }
            }


            applicationSelected = true;
        }

        return applicationSelected;
    }

    protected createNewEmployeeModel(clientId: number): HhsgSharedStuderEmployee {
        let returnEmployeeModel = new HhsgSharedStuderEmployee();

        returnEmployeeModel.applications = [];
        returnEmployeeModel.facilities = [];
        returnEmployeeModel.leaders = [];
        returnEmployeeModel.isDeleted = false;
        returnEmployeeModel.isLeader = false;
        returnEmployeeModel.isRateable = false;
        returnEmployeeModel.isDCMActive = false;
        returnEmployeeModel.isLEMActive = false;
        returnEmployeeModel.departmentId = null;
        returnEmployeeModel.jobTitleId = null;
        returnEmployeeModel.organizationId = this._userAdminServiceBase.ClientsToOrganizations.find(x => x.clientId === clientId).organizationId;

        return returnEmployeeModel;
    }

    protected createNewUserModel(): User {
        var returnUser = new User();

        returnUser.userApplicationModels = [];
        returnUser.userRole = new UserRole();

        return returnUser;
    }

    protected createUserApplicationModel(clientApplication: HhsgSharedClientApplication): HhsgSharedUserApplicationModel {
        let userApplicationModel;

        if (clientApplication) {
            userApplicationModel = new HhsgSharedUserApplicationModel();
            userApplicationModel.clientId = clientApplication.clientId;
            userApplicationModel.clientName = this.clients == null ? '' : this.clients.find(x => x.id === clientApplication.clientId).name;
            userApplicationModel.isActive = true;
            userApplicationModel.applicationId = clientApplication.applicationId;
            userApplicationModel.applicationName = clientApplication.applicationName;
            userApplicationModel.applicationIsActive = clientApplication.isActive;
            userApplicationModel.applicationAbbreviation = clientApplication.applicationAbbreviation;
            userApplicationModel.hasChildApplicationLoginPermission = true;
        }

        return userApplicationModel
    }

    public doesControlHaveError(controlName: string, errorName: string): boolean {
        let controlErrors = this.formGroup.controls[controlName]?.errors;

        return controlErrors && Object.keys(controlErrors).some(s => s == errorName);
    }

    protected getApplicationControlName(clientId: number, applicationId: number): string {
        return `${this.applicationControlPrefix}-${clientId}-${applicationId}`;
    }

    protected async getApplications(): Promise<void> {
        if (!this.applications || this.applications.length == 0) {
            this.applications = await this._clientAdminServiceBase.getAllApplications();
        }

        if (this.isHuronEmployee) {
            this.userApplications = [];

            this.clients.forEach(c => {
                this.applications.forEach(a => {
                    this.userApplications.push({ clientId: c.id, applicationId: a.id});
                });
            });
        }
        else {
            let activeUserApplications = await this._userAdministratorServiceBase.getActiveUserApplications();

            this.userApplications = activeUserApplications?.map(a => ({ clientId: a.clientId, applicationId: a.applicationId }));
        }
    }

    protected async getBaseData(): Promise<void> {
        if(this._userAdminServiceBase.selectedUser && this.isEditing){
            this.userIdFromUrlParam = this._userAdminServiceBase.selectedUser.id;
        }
        if ((!this.activeUser.corporationId || this.isEditing) && this.userIdFromUrlParam > 0) {
            await this._userAdminServiceBase.getAsUserModel(this.userIdFromUrlParam).then((user) => {
                this._userAdminServiceBase.selectedUser = user;
                this.activeUser = { ...this._userAdminServiceBase.selectedUser } as User;
            });
        }

        if (!this.clients || this.clients.length == 0) {
            if (this.isHuronEmployee && (this.activeUser.corporationId || this._userAdminServiceBase.selectedCorporationId)) {
                await this._clientAdminServiceBase.getAllClientsForCorporation((this.activeUser && this.activeUser.corporationId) ||
                    this._userAdminServiceBase.selectedCorporationId).then(async (clients) => {
                        this.clients = clients;
                        await this.getClientsWithApplications();
                        await this.getApplications();
                    });
            }
            else {
                await this._clientAdminServiceBase.getAllClients().then(async (clients) => {
                    this.clients = clients;
                    this.userAdministratorClientIds = this.clients.map(({ id }) => id);
                    await this.getClientsWithApplications();
                    await this.getApplications();
                });
            }
        }
        else {
            await this.getClientsWithApplications();
            await this.getApplications();
        }
    }

    public getClientControlName(id: number): string {
        return `${this.clientControlPrefix}-${id}`;
    }

    protected async getClientsWithApplications(): Promise<void> {
        this._userAdminServiceBase.ClientsWithApplications = [];

        let clientIds = this.clients.map(s => s.id);
        await this._applicationServiceBase.getClientApplicationsByClientIds(clientIds).then(clientApps => {

            for (let client of this.clients) {
                let clientApplications = clientApps.filter(ca => ca.applicationName !== 'Huron Accelerators'
                    && ca.clientId == client.id);

                // next conditional needed because for some reason duplicates are occasionally added
                // and in theory it should not be possible
                if (!this._userAdminServiceBase.ClientsWithApplications.some(c => c.client.id === client.id)) {
                    this.addClientControl(client.id);

                    this._userAdminServiceBase.ClientsWithApplications.push({ client, clientApplications });

                    if (clientApplications && clientApplications.length > 0) {
                        this._userAdminServiceBase.ClientsToOrganizations.push({ clientId: client.id, organizationId: clientApplications[0].childApplicationClientId });
                    }
                }

            }
        });
    }

    /**
     * method to check the status of applications for individual client using user-application mapping
     * @param clientId
     * @param applicationId
     * @returns boolean
     */
    public checkUserApplicationMappingStatus(clientId: number, applicationId: number): boolean {
        return this.userApplications?.some(a => a.applicationId == applicationId && a.clientId == clientId);
    }

    protected async getUserData(): Promise<void> {
        this.selectedEmployee = null;
        var loadedEmployees = false;
        this._userAdminServiceBase.UserEmployees = []; // this drives the tabs data in many ways

        if (this.isAdding) {
            if(!this._userServiceBase.childApplicationUser){
            this.activeUser = this.createNewUserModel();
            this.setDefaultUserRole();
            }
        }

        if (this.isStuderDBAvailable && this.activeUser && this.activeUser.id && !loadedEmployees && !this.isAdding) {
            loadedEmployees = true;

            this._employeeServiceBase.GetEmployeesByEmail(this.activeUser.email)
                .pipe(
                    takeUntil(this._unsubscribe$),
                    catchError((error) => {
                        console.log(error);
                        this._userAdminServiceBase.UserEmployees = [];
                        this.setLoadingStatus(false);
                        return of(undefined);
                    }))
                .subscribe(employees => {
                    if (employees) {
                        employees.forEach(e => {
                            this._userAdminServiceBase.UserEmployees.push(e);
                        });
                    }
                });


        }
        else if (!this.isStuderDBAvailable) {
            this._userAdminServiceBase.UserEmployees = [];
            loadedEmployees = true;
        }

        if (this.activeUser && this.activeUser.userApplicationModels && this.activeUser.userApplicationModels.length > 0) {
            // best to load employees here if !isAdding and that way adding/subtracting studer apps don't cause new employees        
            for (let userApp of this.activeUser.userApplicationModels) {
                // basically forced this to be synchronous
                if (this.isHuronEmployee ||
                    (this.userAdministratorClientIds && this.userAdministratorClientIds.find(clientid => clientid == userApp.clientId))) {
                    if (userApp.isActive &&
                        (this.isHuronEmployee || (userApp.applicationAbbreviation !== Constants.HuronAcceleratorsAbbreviation))) {
                        let localClientApp: HhsgSharedClientApplication;

                        if (userApp.applicationAbbreviation === Constants.HuronAcceleratorsAbbreviation) {
                            continue;
                        }

                        if (this._userAdminServiceBase.ClientsWithApplications && this._userAdminServiceBase.ClientsWithApplications.length > 0) {
                            var clientWithApplications = this._userAdminServiceBase.ClientsWithApplications.find(x => x.client.id == userApp.clientId);

                            if (clientWithApplications) {
                                localClientApp = clientWithApplications.clientApplications.find(x =>
                                    x.clientId == userApp.clientId &&
                                    x.applicationId == userApp.applicationId);
                            }
                        }

                        let clientApp: HhsgSharedClientApplication = new HhsgSharedClientApplication();

                        if (localClientApp) {
                            clientApp = localClientApp;
                        }

                        clientApp.clientId = userApp.clientId;
                        clientApp.applicationAbbreviation = userApp.applicationAbbreviation;
                        clientApp.applicationName = userApp.applicationName;
                        clientApp.applicationId = userApp.applicationId;
                        clientApp.childApplicationClientId = localClientApp && localClientApp.childApplicationClientId ? localClientApp.childApplicationClientId : 0;

                        if (!this.isAdding && this.isStuderDBAvailable && this.isStuderApp(clientApp.applicationAbbreviation)) {
                            // at this point we know we have at least one employee
                            this.selectedEmployee = null;

                            // new code start
                            if (loadedEmployees || (this._userAdminServiceBase.UserEmployees && this._userAdminServiceBase.UserEmployees.length > 0)) {
                                this.selectedEmployee = this._userAdminServiceBase.UserEmployees.find(x => x.organizationId === clientApp.childApplicationClientId);
                                if (!this.selectedEmployee) {
                                    this.selectedEmployee = this.createNewEmployeeModel(clientApp.clientId);
                                    this._userAdminServiceBase.UserEmployees.push(this.selectedEmployee);
                                }
                            }
                            else {
                                if (!this.isStuderDBAvailable && !loadedEmployees) {
                                    loadedEmployees = true;

                                    this._employeeServiceBase.GetEmployeesByEmail(this.activeUser.email)
                                        .pipe(
                                            takeUntil(this._unsubscribe$),
                                            catchError((error) => {
                                                console.log(error);
                                                this.selectedEmployee = this.createNewEmployeeModel(clientApp.clientId);
                                                this._userAdminServiceBase.UserEmployees.push(this.selectedEmployee);
                                                this.setLoadingStatus(false);
                                                return of(undefined);
                                            }))
                                        .subscribe(employees => {
                                            if (employees) {
                                                employees.forEach(e => {
                                                    this._userAdminServiceBase.UserEmployees.push(e);

                                                    if (this._userAdminServiceBase.UserEmployees && this._userAdminServiceBase.UserEmployees.length > 0) {
                                                        this.selectedEmployee = this._userAdminServiceBase.UserEmployees.find(h => h.organizationId === clientApp.childApplicationClientId);
                                                    }
                                                });

                                                if (!this.selectedEmployee) {
                                                    this.selectedEmployee = this.createNewEmployeeModel(clientApp.clientId);
                                                    this._userAdminServiceBase.UserEmployees.push(this.selectedEmployee);
                                                }
                                            }
                                        });
                                }
                                else {
                                    // so we've loaded employees already and failed to find one so we have to create one apparently... i don't think this can happen
                                    this.selectedEmployee = this.createNewEmployeeModel(clientApp.clientId);
                                    this._userAdminServiceBase.UserEmployees.push(this.selectedEmployee);
                                }
                            }

                            this._userAdminServiceBase.ClientsWithApplications.forEach(clApp => {
                                if (clApp.clientApplications.findIndex(x => x.applicationId === userApp.applicationId &&
                                    x.clientId === userApp.clientId) > -1) {
                                    clientApp.id = clApp.clientApplications.find(x =>
                                        x.applicationId === userApp.applicationId &&
                                        x.clientId === userApp.clientId).id;
                                }
                            });

                            this.addedClientApplications.push(clientApp);
                            this.updateClientCheckChanged(true, userApp.clientId);
                        }
                        else {
                            if (!this.isAdding) {
                                this.addedClientApplications.push(clientApp);
                                this.updateClientCheckChanged(true, userApp.clientId);
                            }
                        }
                    }
                }
            }
        }
    }

    public isApplicationChecked(clientId: number, applicationId: number): boolean {
        if (!this.activeUser.userApplicationModels) {
            return false;
        }

        if (this.isEditing) {
            return this.activeUser.userApplicationModels.some(u => u.applicationId == applicationId &&
                u.clientId == clientId &&
                u.isActive);
        }
        else if (this.isAdding) {
            return this.addedClientApplications &&
                this.addedClientApplications.length > 0 &&
                this.addedClientApplications.some(h => h.clientId == clientId && h.applicationId == applicationId);
        }
        else {
            return this.activeUser.userApplicationModels.some(h => h.applicationId === applicationId && h.clientId === clientId);
        }
    }

    public isClientChecked(clientId: number): boolean {
        if (!this._userAdminServiceBase.UserClients) {
            return false;
        }

        return this._userAdminServiceBase.UserClients.some(c => c.client.id == clientId);
    }

    public isDisabled(): boolean {
        return false;
    }

    public isFormDirty(): boolean {
        if (this.getIsFormGroupDirty()) {
            // If the form is dirty then hide all the tabs that are saved to the user.
            this.activeUser?.userApplicationModels?.filter(u => u.isActive && u.applicationIsActive).forEach(u => {
                this._tabControlServiceBase.announceTabHidden(u.applicationAbbreviation);
            });

            // If the form is dirty then hide all the tabs that have been added but not saved.
            this.addedClientApplications.forEach(a => {
                this._tabControlServiceBase.announceTabHidden(a.applicationAbbreviation);
            });
        }
        else {
            // Get a list of the applications saved to the user.  It is not necessary to announce the ones added since the last save
            // because all tabs are hidden when the form is dirty.
            let visibleApplications = this.activeUser?.userApplicationModels
                                                     ?.filter(u => 
                                                        u.isActive && 
                                                        u.applicationIsActive && 
                                                        this.checkUserApplicationMappingStatus(u.clientId, u.applicationId))
                                                     ?.map(u => u.applicationAbbreviation);

            // If the form is not dirty then show all the tabs that are saved to the user.
            visibleApplications?.forEach(abbreviation => {
                this._tabControlServiceBase.announceTabVisible(abbreviation);
            });
        }

        return this.getIsFormGroupDirty();
    }

    public get isHuronEmployee(): boolean {
        if (!this.loggedInUserProfile) {
            return false;
        }

        return RoleUtil.isHuronEmployee(this.loggedInUserProfile.roles);
    }

    public isLoggedUserAppPermission(appId: number) {
        return this.userAdministratorApplicationIds?.some(a => a == appId);
    }

    public isLoginMethodNone(): boolean {
        if (this._userAdminServiceBase && this._userAdminServiceBase.selectedCorporation) {
            return (this._userAdminServiceBase.selectedCorporation.loginMethod === LoginMethodSettingConstants.None)
        }
        else if (this._userAdminServiceBase.selectedUserCorporation) {
            return (this._userAdminServiceBase.selectedUserCorporation.loginMethod === LoginMethodSettingConstants.None)
        }

        return false;
    }

    protected isStuderApp(applicationAbbreviation: string) {
        return (this.studerAbbreviations.indexOf(applicationAbbreviation) > -1);
    }

    protected async loadData(): Promise<void> {
        // more dealing with async calls galore but after this we're good to go
        // and have dealt with the necessary dependencies properly
        if (this._userAdminServiceBase.confirmSaving) {
            return;
        }

        this.setLoadingStatus(true);

        this.removeAllApplicationControls();
        this.removeAllClientControls();

        this.addedApplications = [];
        this._userAdminServiceBase.UserClients = [];
        this._userAdminServiceBase.UserEmployees = [];

        await this.getBaseData().then(async () => {
            if (this._userAdminServiceBase.selectedUser) {
                this.activeUser = { ...this._userAdminServiceBase.selectedUser } as User;
            }
            else if (this.userIdFromUrlParam > 0) {
                this.setActiveUserIfUndefined();
            }
            else if (this._userServiceBase.childApplicationUser) {
                this.getChildApplicationUserDetails();
            }
            else {
                this.activeUser = new User();
                this.setDefaultUserRole();
            }
            await this.getUserData();

            this.initializeControls();
            
            this._changeDetectorBase.detectChanges();

            this.setLoadingStatus(false);
        });
    }

    public onClientCheckChanged(clientInput: HTMLInputElement, clientId: number): void {
        this.formGroup.controls[this.getClientControlName(clientId)].setValue(clientInput.checked);
        this._formStateServiceBase.isFormDirty = true;

        this.updateClientCheckChanged(clientInput.checked, clientId);

        if (this.isAdding == true && this.duplicateEmailError == true) {
            this.formGroup.controls['email'].setErrors({ 'duplicate': true });
            this.isEmailValid = false;
        }
    }
    
    public isFormValid(): boolean {
        return this.formGroup.valid;
    }

    public async reload(): Promise<void> {
        await this.loadData();
    }

    // need to remove employee for user or deactivate somehow
    // later the employees are enumerated to determine a number
    // of things and at that point the employee record won't
    // really be valid
    protected removeAddedClientApps(clientId: number) {
        var i = this.addedClientApplications.length;

        while (i--) {
            if (this.addedClientApplications[i].clientId === clientId) {
                this.addedClientApplications.splice(i, 1);
            }
        }

        if (this.activeUser &&
            this.activeUser.userApplicationModels &&
            this.activeUser.userApplicationModels.length &&
            this.activeUser.userApplicationModels.length > 0) {
            i = this.activeUser.userApplicationModels.length;
            while (i--) {
                let userApplication = this.activeUser.userApplicationModels[i];

                if(userApplication.clientId == clientId) {
                   this.removeApplicationControl(userApplication.clientId, userApplication.applicationId);                

                   if (userApplication && this.activeUser.clientId === clientId && this.activeUser.roleName != Constants.User) {
                    let isActive = false;

                    if (userApplication.applicationId === 0 && userApplication.applicationAbbreviation === Constants.HuronAcceleratorsAbbreviation) {
                        isActive = true;
                    }
                    else if (!this.isHuronEmployee &&
                        this.userAdministratorApplicationIds &&
                        this.userAdministratorApplicationIds.indexOf(userApplication.applicationId) === -1) {
                        isActive = this.activeUser.userApplicationModels[i].isActive;
                    }

                    this.activeUser.userApplicationModels[i].isActive = isActive;
                }
            }
            }
        }

        let organizationId = this._userAdminServiceBase.clientIdToOrganizationId(clientId);

        if ((organizationId > 0) &&
            (this._userAdminServiceBase.UserEmployees.findIndex(x => x.organizationId === organizationId) > -1)) {
            this._userAdminServiceBase.UserEmployees.find(x => x.organizationId === organizationId).isDeleted = true;
        }
    }

    protected removeAllApplicationControls(): void {
        Object.keys(this.formGroup.controls).filter(c => c.startsWith(this.applicationControlPrefix)).forEach(c => {
            this.formGroup.removeControl(c);
        });
        this.addedClientApplications = [];
    }

    protected removeAllClientControls(): void {
        Object.keys(this.formGroup.controls).filter(c => c.startsWith(this.clientControlPrefix)).forEach(c => {
            this.formGroup.removeControl(c);
        });
    }

    protected removeApplicationControl(clientId: number, applicationId: number): void {
        let controlName = this.getApplicationControlName(clientId, applicationId);
        let control = this.formGroup.controls[controlName];

        if (control) {
            this.formGroup.removeControl(controlName);
        }
    }

    protected resetMessages(): void {
        this.errorMessages = [];
    }

    public async save(): Promise<number> {
        if(!this.isEmailValid && this.isAdding){
            return -1;
        }
        this.isLoading = true;
        this.errorMessages = [];
        this._userAdminServiceBase.isSubmitted = true;
        this.runExtraValidation();

        // check for the selected clients. if there are no clients selected display error
        if (this.formGroup && !this.formGroup.valid) { // if the user form is not valid
            this.errorMessages.push(Constants.ValidationFailure);
            this.setLoadingStatus(false);
            return -1;
        }
        else if (this._userAdminServiceBase.UserClients.length === 0 && (this.isAdding || this.isEditing)) {
            // if user form is valid and no clients are selected
            // display alert message to select atleast one client
            this.errorMessages.push("At least one Client must be selected.");
        }
        else if (this.activeUser.userRole.roleName.toUpperCase() !== Constants.HuronAdministrator.toUpperCase() &&
            (this.isHuronEmployee && !this.addedClientApplications ||
                (this.isAdding && !this.isHuronEmployee && !this.addedClientApplications) ||
                !this.checkAtleastOneApplicationSelectedForSelectedClient())) {
            this.errorMessages.push("At least one Application must be selected for each Client.");
        }

        if (this.errorMessages.length > 0) {
            this.setLoadingStatus(false);
            return -1;
        }

        this._userAdminServiceBase.confirmSaving = !this.errorMessages || this.errorMessages.length == 0;

        let saveResult = await this.saveData();

        if (saveResult) {
            this._userAdminServiceBase.announceActiveUser(this.activeUser);
            this._userAdminServiceBase.selectedUser = this.activeUser;
            this._userServiceBase.convertChildApplicationUserToHapUser = false;
            this.isEditing = true;
            this.isAdding = false;
            this.errorMessages = [Constants.SaveSuccess];
            this.formGroup.markAsPristine();
            this.afterSaveSuccess();
            await this.reload();
            this.setLoadingStatus(false);
            return 1;
        }
        else {
            if(this.errorMessages.length == 0) {
              this.errorMessages.unshift("An error occurred during save.");
            }
            this.setLoadingStatus(false);
            return -1;
        }
    }

    protected runExtraValidation() : void {
        // Do nothing, but allow for child class overrides
    }

    protected afterSaveSuccess() : void {
        // Do nothing, but allow for child class overrides
    }

    protected async saveData(): Promise<boolean> {
        if (!this.activeUser.isNonSSOUser && !this.isLoginMethodNone()) {
            this.activeUser.shouldResetPassword = false;
        }

        this.userApplicationModels = [];

        if (!this.isEditing) {
            this.activeUser.clientId = this._userAdminServiceBase.UserClients[0].client.id;
            this.activeUser.ChildApplicationClientId = 0;  // As per Api requirement, this value need to pass zero everytime.

            for (let clientApp of this.addedClientApplications) {
                let userAppModel = this.createUserApplicationModel(clientApp);

                if (this._userAdminServiceBase.UserEmployees &&
                    this._userAdminServiceBase.UserEmployees.length > 0 &&
                    userAppModel.applicationAbbreviation === Constants.LEMAbbreviation) {
                    userAppModel.hasChildApplicationLoginPermission = this._userAdminServiceBase.UserEmployees[0].isLeader;
                }

                this.userApplicationModels.push(userAppModel);
            }

            this.activeUser.userApplicationModels = this.userApplicationModels;
        }
        this.setUserRole();

        let UserProfile;

        if (this.isEditing) {
            for (let clientApp of this.addedClientApplications) {
                let userAppModel = this.createUserApplicationModel(clientApp);

                if (this.selectedEmployee && userAppModel.applicationAbbreviation === Constants.LEMAbbreviation) {
                    userAppModel.hasChildApplicationLoginPermission = this.selectedEmployee.isLeader;
                }

                var idex: number = -1;

                if (this.activeUser.userApplicationModels &&
                    this.activeUser.userApplicationModels.length &&
                    this.activeUser.userApplicationModels.length > 0) {
                    idex = this.activeUser.userApplicationModels.findIndex(x => x.applicationId === userAppModel.applicationId && x.clientId === userAppModel.clientId);
                }
                else if (!this.activeUser.userApplicationModels) {
                    this.activeUser.userApplicationModels = [];
                }

                if (idex === -1) {
                    this.activeUser.userApplicationModels.push(userAppModel);
                }
                else {
                    // 28982: set the childApplcationsUserId to the new app model instance from the pre-existing one. Otherwise it gets set to 0
                    userAppModel.childApplicationUserId = this.activeUser.userApplicationModels[idex].childApplicationUserId;
                    userAppModel.customClaims = this.activeUser.userApplicationModels[idex].customClaims;
                    this.activeUser.userApplicationModels[idex] = userAppModel;
                }

                this.userApplicationModels.push(userAppModel);
            }

            if (this.activeUser.userApplicationModels &&
                this.activeUser.userApplicationModels.length &&
                this.activeUser.userApplicationModels.length > 0) {
                var isUserAministratorHasAccess = this.isHuronEmployee;

                for (let userApp of this.activeUser.userApplicationModels) {
                    if (this.userApplicationModels.findIndex(x => x.applicationId === userApp.applicationId && x.clientId === userApp.clientId) === -1) {
                        if (userApp.applicationId === 0 && userApp.applicationAbbreviation === Constants.HuronAcceleratorsAbbreviation) {
                            userApp.isActive = true;
                        }
                        else if (!isUserAministratorHasAccess && !this.userAdministratorClientIds.find(clientId => clientId === userApp.clientId)) {
                            userApp.isActive = userApp.isActive;
                        }
                        else if (!isUserAministratorHasAccess && this.userAdministratorClientIds.find(clientId => clientId === userApp.clientId) &&
                            !this.userAdministratorApplicationIds.find(applicationId => applicationId === userApp.applicationId)) {
                            userApp.isActive = userApp.isActive;
                        }
                        else {
                            userApp.isActive = false;
                        }
                    }
                }
            }

            UserProfile = this._userAdminServiceBase.updateUserProfile(this.activeUser);
            this._userAdminServiceBase.formStatus = 'Your changes were successfully saved.';
            this.isShowDormant = this.activeUser.isDormant && this.activeUser.isActive;
        }
        else {
            UserProfile = this._userAdminServiceBase.createNewUser(this.activeUser);
            this._userAdminServiceBase.formStatus = 'Your changes were successfully saved.';
        }

        await UserProfile.then(async (user) => {
            this._activeUser = user;
            this.updateUserAfterSubmit(user);
            this._userAdminServiceBase.confirmSaving = false;
            this._formStateServiceBase.reset();
        },
            (error: { error: string; }) => {
                this._userAdminServiceBase.confirmSaving = false;
                this.errorMessages.push(error.error);
                window.scroll(0, 0);
                this._userAdminServiceBase.formStatus = undefined;
            });
        return !this.errorMessages || this.errorMessages.length == 0;
    }

    protected setActiveUserIfUndefined(): void {
        this._userAdminServiceBase.getAsUserModel(this.userIdFromUrlParam).then((user) => {
            this._userAdminServiceBase.selectedUser = user;
            this.activeUser = { ...this._userAdminServiceBase.selectedUser } as User;
            this.initializeControls();

            if (this._userAdminServiceBase.selectedCorporation == null && !this.corporations) {
                this._corporationServiceBase.getAllActiveCorporations().then((corporations) => {
                    this.corporations = corporations;
                    this._userAdminServiceBase.selectedUserCorporation = this.corporations.find(x => x.id === user.corporationId);
                });
            }
        });
    }

    protected setDefaultUserRole(): void {
        var userRole = UserRoles.find(e => e.name.toUpperCase() === Constants.User.toUpperCase());

        if (userRole) {
            this.activeUser.userRole = {
                roleId: userRole.roleId,
                roleName: userRole.name,
                friendlyName: userRole.name
            };
        }
        else {
            throw new Error(`User role is not present`);
        }
    }

    protected setLoadingStatus(isLoading: boolean): void {
        this.isLoading = isLoading; 
        if (isLoading) {
            this._tabControlServiceBase.announceTabLoading(this.tab?.id);
            this.formGroup.disable();
        }
        else {
            this._tabControlServiceBase.announceTabFinishedLoading(this.tab?.id);
            this.initializeControls();
        }
    }
    
    protected setUserRole(): void {
        let roleName = this.activeUser.userRole.roleName;

        if (this.activeUser.userRole) {
            this.activeUser.roleName = roleName;
            var userRole = UserRoles.find(e => e.name.toUpperCase() === roleName.toUpperCase());

            if (userRole) {
                this.activeUser.userRole.roleId = userRole.roleId;
            }
            else {
                throw new Error(`User role ` + roleName + ` is not present`);
            }
        }
    }

    public setValue(clientAppModel: HhsgSharedClientApplication): boolean {
        if (!this.activeUser.userApplicationModels) {
            return false;
        }

        if (this.isEditing) {
            let application = this.activeUser.userApplicationModels.find(u =>
                u.applicationId == clientAppModel.applicationId &&
                u.clientId == clientAppModel.clientId &&
                u.isActive);

            if (application) {
                return true;
            }

            return false;
        }
        else if (this.isAdding) {
            if (this.addedClientApplications && this.addedClientApplications.length > 0) {
                return (this.addedClientApplications.filter(x => x.id === clientAppModel.id).length > 0);
            }
            else {
                return false;
            }
        }
        else {
            return (this.activeUser.userApplicationModels.findIndex(x => x.applicationId === clientAppModel.applicationId &&
                x.clientId === clientAppModel.clientId) > -1);
        }
    }

    protected updateClientCheckChanged(checked: boolean, clientId: number): void {
        if (checked && !this._userAdminServiceBase.UserClients.some(c => c.client.id == clientId)) {
            let client = this.clients.find(c => c.id == clientId);

            if (!client) {
                return;
            }

            this.addAClient(clientId);

            let ind = -1;

            if (this._userAdminServiceBase.UserClients && this._userAdminServiceBase.UserClients.length > 0) {
                ind = this._userAdminServiceBase.UserClients.findIndex(c => c.client.id === clientId);
            }

            if (ind === -1) {
                let index = this._userAdminServiceBase.ClientsWithApplications.findIndex(c => c.client.id === clientId);
                this._userAdminServiceBase.UserClients.push({ client, index });
            }

            this._userAdminServiceBase.ClientsWithApplications.filter(c => c.client.id == clientId).flatMap(c => c.clientApplications).forEach(a => {
                this.addApplicationControl(clientId, a.applicationId);
            });
        }
        else if (!checked) {
            this.removeAddedClientApps(clientId);

            if (this._userAdminServiceBase.UserClients) {
                this._userAdminServiceBase.UserClients = this._userAdminServiceBase.UserClients.filter(c => c.client.id !== clientId);
            }
        }
        
        this._changeDetectorBase.detectChanges();
    }

    protected updateUserAfterSubmit(user: User): void {
        this.activeUser = user;
        this.errorMessages = [];
    }

    public userApplicationChecked(appInput?: HTMLInputElement, applicationId?: number, clientAppModel?: HhsgSharedClientApplication, cIndex?: number): void {
        let id: number;

        if (appInput) {
            id = +appInput.value;
        }
        else {
            id = +applicationId;
        }

        let clickedApplication: HhsgSharedApplication;

        if (appInput) {
            clickedApplication = this.applications.find(x => x.id === clientAppModel.applicationId);

            if (appInput.checked) {
                if (this.addedClientApplications.findIndex(x => x.applicationId === clientAppModel.applicationId && x.clientId === clientAppModel.clientId) === -1) {
                    this.addedClientApplications.push(clientAppModel);
                }
            }
            else {
                this.addedClientApplications.splice(this.addedClientApplications.findIndex(x => x.applicationId === clientAppModel.applicationId && x.clientId === clientAppModel.clientId), 1);
            }
        }
        else {
            clickedApplication = this.applications.find(x => x.id === id);
        }

        if (!clickedApplication) {
            clickedApplication = this.applications.find(x => x.id === id);
        }

        if (appInput && appInput.checked) {
            this.addedApplications.push(clickedApplication);
        }

        this._formStateServiceBase.isFormDirty = true;
    }

    //When user change the Role we need to reset because using roleName save is handled.
    public resetRole(): void {
        if (this.editableUserRoles && this.activeUser.userRole) {
            this.activeUser.userRole.roleName = this.editableUserRoles.find(e => e.roleId == this.activeUser.userRole.roleId).roleName;
        }
    }
    
    getChildApplicationUserDetails() {
        if (this._userServiceBase.convertChildApplicationUserToHapUser) {
            if (this._userServiceBase.childApplicationUser != null) {
                this.activeUser = this._userServiceBase.childApplicationUser;
            }
        }
        this.setDefaultUserRole();
    }
}
